"use client";

import { captureNativeViewport } from "@/lib/native/instants";

type Preview = {
  image: HTMLImageElement;
  width: number;
  height: number;
  dark: boolean;
  capturedAt: number;
  bytes: number;
};

/** Bounded, session-only images. No localStorage, uploads, or hidden React DOM. */
export class ViewportPreviews {
  private readonly entries = new Map<string, Preview>();

  clear() {
    this.entries.clear();
  }

  set(href: string, preview: Preview) {
    if (preview.bytes > 6_000_000) return;
    this.entries.delete(href);
    this.entries.set(href, preview);
    let bytes = [...this.entries.values()].reduce(
      (sum, item) => sum + item.bytes,
      0,
    );
    for (const [key, item] of this.entries) {
      if (this.entries.size <= 4 && bytes <= 12_000_000) break;
      this.entries.delete(key);
      bytes -= item.bytes;
    }
  }

  get(href: string | null): HTMLImageElement | null {
    if (!href) return null;
    const preview = this.entries.get(href);
    if (
      !preview ||
      Date.now() - preview.capturedAt > 5 * 60_000 ||
      preview.width !== window.innerWidth ||
      preview.height !== window.innerHeight ||
      preview.dark !== document.documentElement.classList.contains("dark")
    )
      return null;
    return preview.image;
  }
}

export function localPreviewHref(raw: string | null): string | null {
  if (!raw) return null;
  try {
    const url = new URL(raw, window.location.href);
    if (url.origin !== window.location.origin) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

/** Capture after paint/content/scroll settles, never on the gesture frame. */
export function trackViewportPreviews(
  page: HTMLElement,
  canCapture: () => boolean,
) {
  const previews = new ViewportPreviews();
  let timer = 0;
  let inFlight = false;
  let disposed = false;
  let protectedScreen = false;
  let generation = 0;
  let lastCaptureAt = 0;
  const isAuthRoute = () =>
    /^\/(auth|login|sign-in|sign-up|onboarding)(\/|$)/.test(
      window.location.pathname,
    );
  const eligible = () =>
    !disposed &&
    !protectedScreen &&
    document.visibilityState === "visible" &&
    !isAuthRoute() &&
    canCapture();

  const capture = async () => {
    timer = 0;
    if (!eligible() || inFlight) return;
    const href = localPreviewHref(window.location.href);
    if (!href) return;
    const token = generation;
    const width = window.innerWidth;
    const height = window.innerHeight;
    const dark = document.documentElement.classList.contains("dark");
    inFlight = true;
    lastCaptureAt = Date.now();
    try {
      const dataUrl = await captureNativeViewport();
      if (!dataUrl || dataUrl.length > 2_000_000) return;
      const image = new Image();
      image.alt = "";
      image.draggable = false;
      image.src = dataUrl;
      await image.decode();
      if (
        !eligible() ||
        token !== generation ||
        href !== localPreviewHref(window.location.href)
      )
        return;
      previews.set(href, {
        image,
        width,
        height,
        dark,
        capturedAt: Date.now(),
        // Include both decoded pixels and the UTF-16 data URL, not just JPEG.
        bytes:
          image.naturalWidth * image.naturalHeight * 4 + dataUrl.length * 2,
      });
    } catch {
      // A missing snapshot never blocks back navigation.
    } finally {
      inFlight = false;
    }
  };
  const schedule = () => {
    generation += 1;
    window.clearTimeout(timer);
    timer = window.setTimeout(
      () => void capture(),
      Math.max(300, 1000 - (Date.now() - lastCaptureAt)),
    );
    if (isAuthRoute()) previews.clear();
  };
  const clear = () => {
    generation += 1;
    previews.clear();
    window.clearTimeout(timer);
  };
  const visibility = () => {
    if (document.visibilityState === "hidden") clear();
    else schedule();
  };
  const protection = (event: Event) => {
    protectedScreen = Boolean((event as CustomEvent<boolean>).detail);
    clear();
    if (!protectedScreen) schedule();
  };
  const observer = new MutationObserver(schedule);
  observer.observe(page, {
    childList: true,
    subtree: true,
    attributes: true,
    characterData: true,
    // Motion writes transform styles every frame; those must not keep a
    // settled route's capture timer alive or wake a native capture loop.
    attributeFilter: ["class", "src", "hidden", "data-state"],
  });
  const appearance = new MutationObserver(() => {
    clear();
    schedule();
  });
  appearance.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class"],
  });
  page.addEventListener("load", schedule, true);
  page.addEventListener("loadeddata", schedule, true);
  window.addEventListener("scroll", schedule, { capture: true, passive: true });
  window.addEventListener("resize", clear);
  window.addEventListener("hiive:screen-protection", protection);
  document.addEventListener("visibilitychange", visibility);
  schedule();
  return {
    previews,
    dispose() {
      disposed = true;
      clear();
      observer.disconnect();
      appearance.disconnect();
      page.removeEventListener("load", schedule, true);
      page.removeEventListener("loadeddata", schedule, true);
      window.removeEventListener("scroll", schedule, true);
      window.removeEventListener("resize", clear);
      window.removeEventListener("hiive:screen-protection", protection);
      document.removeEventListener("visibilitychange", visibility);
    },
  };
}
